import React, { FC, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Skeleton } from '@ui';
import {
  NumberChartResponse,
  ReportChart,
  ReportChartResponse,
  ReportTimeRange,
} from '@app/interfaces/report.type';
import { navigationRoutes } from '@app/utils/navigation-routes';
import { useGetDashboardQuery } from '@app/store/api/dashboard-page.api';
import { FilterItem } from '@app/interfaces/filter';

import { useAppSelector } from '@app/store/store';

import { ChartsLayoutType } from '@app/interfaces/dashboards.type';

import ChartMainRenderer from './chart-main-renderer';
import ChartHeader from './chart-header';

export type ChartConfig = { dataPointIndex: string | number };

type ChartComponentProps = {
  data: ReportChartResponse['data'];
  boxBackground?: string;
  chartId?: string;
  chart_widget_info?: { widget_id: string; chart_widget_id: string };
  dashboard_graph_id?: string;
  dashboard?: boolean;
  widgetMode?: boolean;
  commonRange?: ReportTimeRange | null;
  viewOnly?: boolean;
  disableDataPointClick?: boolean;
  isDraggable?: boolean;
  layoutSettings: ChartsLayoutType[];
  setOpenedCustomDateModal?: React.Dispatch<React.SetStateAction<boolean>>;
  setMobile?: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenRemoveModal?: React.Dispatch<React.SetStateAction<boolean>>;
  setData?: React.Dispatch<React.SetStateAction<ReportChartResponse['data'] | undefined>>;
  // setData?: React.Dispatch<React.SetStateAction<ReportChart | undefined>>;
  filterValue?: FilterItem[];
  chartWidth?: number;
  chartHeight?: number;
  handleChangeWidgetChartInterval?(
    interval: string,
    widget_info: { widget_id: string; chart_widget_id: string } | undefined,
  ): void;
  currentTemplate: 'chats' | 'chat' | 'voice';
  onEditChartClick?(data: ReportChart | NumberChartResponse): void;
};

const ChartBlock: FC<ChartComponentProps> = (props) => {
  const {
    data,
    dashboard,
    viewOnly,
    setOpenedCustomDateModal,
    setOpenRemoveModal,
    chart_widget_info,
    setMobile,
    setData,
    filterValue,
    chartWidth,
    boxBackground,
    layoutSettings,
    currentTemplate,
    dashboard_graph_id,
    disableDataPointClick,
    handleChangeWidgetChartInterval,
    chartHeight,
    widgetMode,
    onEditChartClick,
  } = props;

  const { userSettings } = useAppSelector((state) => state.userSettings);

  const { chartLoading: chartLoadingId } = useAppSelector((state) => state.reportCharts);
  const { currentData: currentDashboard } = useGetDashboardQuery(
    userSettings?.currentDashboardId || '',
    { skip: !dashboard },
  );

  const chartLoading = useMemo(() => chartLoadingId, [chartLoadingId]);
  const [visibleToolbarState, changeVisibleToolbarState] = useState(false);

  const navigate = useNavigate();

  const handleEditChart = ({ key }: { key: string }) => {
    switch (key) {
      case 'edit': {
        if (widgetMode) {
          onEditChartClick?.(data);
          return;
        }
        setData && setData(data);
        setMobile && setMobile(true);
        break;
      }
      case 'toolbar': {
        changeVisibleToolbarState((prev) => !prev);
        break;
      }
      case 'remove': {
        setData && setData(data);
        setOpenRemoveModal && setOpenRemoveModal(true);
        break;
      }
      case 'to_report': {
        currentTemplate === 'chats' || currentTemplate === 'chat'
          ? navigate(`/${navigationRoutes.chatReports}/${data.live_report_id}`)
          : navigate(`/${navigationRoutes.reports}/${data.live_report_id}`);
        break;
      }
    }
  };

  if (!dashboard && chartLoading === data.graph_id)
    return <Skeleton width={chartWidth} height={chartHeight} />;
  if (!data) return null;
  return (
    <Box
      style={boxBackground && !data.errorStatus ? { backgroundColor: boxBackground } : {}}
      className={`${data.errorStatus && '!bg-basic_red/[.4]'} min-w-full h-full   ${
        widgetMode ? '!shadow-transparent pb-10' : 'pb-16 pt-4'
      }`}
    >
      <div className="h-full">
        <ChartHeader
          setOpenedCustomDateModal={setOpenedCustomDateModal}
          layoutSettings={layoutSettings}
          currentTemplate={currentTemplate}
          chartHeight={chartHeight || 0}
          chartWidth={chartWidth || 0}
          data={data}
          handleChangeWidgetChartInterval={handleChangeWidgetChartInterval}
          chart_widget_info={chart_widget_info}
          viewOnly={viewOnly || false}
          visibleToolbarState={visibleToolbarState}
          widgetMode={widgetMode || false}
          handleEditChart={handleEditChart}
          dashboard={dashboard || false}
          currentDashboard={currentDashboard}
          setData={setData}
          dashboard_graph_id={dashboard_graph_id}
          filterValue={filterValue}
        />
        <ChartMainRenderer
          data={data}
          visibleToolbarState={visibleToolbarState}
          isNumber={data.type === 'number'}
          widgetMode={widgetMode || false}
          chartWidth={chartWidth || 0}
          currentTemplate={currentTemplate}
          disableDataPointClick={disableDataPointClick || false}
          chartHeight={chartHeight || 0}
        />
      </div>
    </Box>
  );
};

export default React.memo(ChartBlock, (prevProps, nextProps) => {
  return (
    prevProps.data === nextProps.data &&
    prevProps.chartId === nextProps.chartId &&
    prevProps.dashboard_graph_id === nextProps.dashboard_graph_id &&
    prevProps.dashboard === nextProps.dashboard &&
    prevProps.commonRange === nextProps.commonRange &&
    prevProps.viewOnly === nextProps.viewOnly &&
    prevProps.isDraggable === nextProps.isDraggable &&
    prevProps.layoutSettings === nextProps.layoutSettings &&
    prevProps.setOpenedCustomDateModal === nextProps.setOpenedCustomDateModal &&
    prevProps.setMobile === nextProps.setMobile &&
    prevProps.setOpenRemoveModal === nextProps.setOpenRemoveModal &&
    prevProps.setData === nextProps.setData &&
    prevProps.filterValue === nextProps.filterValue &&
    prevProps.chartWidth === nextProps.chartWidth &&
    prevProps.chartHeight === nextProps.chartHeight &&
    prevProps.currentTemplate === nextProps.currentTemplate
  );
});
